import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import {
    Add,
    Delete,
    Edit,
} from "@mui/icons-material";

import apiClient from "../utils/apiClient";

import { toast } from "react-toastify";
import { imageBaseurl } from "../utils/common";

const initialForm = {
    title: "",
    short_description: "",
    sport_type: "",
    image: "",
    redirect_url: "",
    year: "",
    is_live: false,
    live_start_time: "",
    button_text: "",
    button_url: "",
    status: true,
};

export default function SliderManagementPage() {

    const [sliders, setSliders] =
        useState([]);

    const [loading, setLoading] =
        useState(false);

    const [open, setOpen] =
        useState(false);

    const [editId, setEditId] =
        useState(null);

    const [formData, setFormData] =
        useState(initialForm);

    const [search, setSearch] =
        useState("");

    const [deleteDialog, setDeleteDialog] =
        useState(false);

    const [deleteId, setDeleteId] =
        useState(null);

    // ====================================
    // GET SLIDERS
    // ====================================
    const getSliders = async () => {

        try {

            setLoading(true);

            const response =
                await apiClient.get(
                    "/api/admin/home-slider"
                );

            const result =
                response?.data;

            if (result?.success) {
                setSliders(
                    result.data || []
                );
            }

        } catch (error) {

            console.log(error);

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {
        getSliders();
    }, []);

    // ====================================
    // HANDLE CHANGE
    // ====================================
    const handleChange = (e) => {

        const {
            name,
            value,
            checked,
            type,
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : value,
        }));
    };

    // ====================================
    // IMAGE UPLOAD
    // ====================================
    const handleImageUpload = (
        e
    ) => {

        const file =
            e.target.files[0];

        if (!file) return;

        if (
            !file.type.startsWith(
                "image/"
            )
        ) {

            return toast.error(
                "Please select image file"
            );
        }

        if (
            file.size >
            2 * 1024 * 1024
        ) {

            return toast.error(
                "Image size must be less than 2MB"
            );
        }

        setFormData((prev) => ({
            ...prev,
            image: file,
        }));
    };

    // ====================================
    // VALIDATION
    // ====================================
    const validateForm = () => {

        if (
            !formData.title?.trim()
        ) {

            toast.error(
                "Title is required"
            );

            return false;
        }

        if (
            !formData.short_description?.trim()
        ) {

            toast.error(
                "Description is required"
            );

            return false;
        }

        if (
            !formData.sport_type
        ) {

            toast.error(
                "Sport type is required"
            );

            return false;
        }

        if (
            !formData.year
        ) {

            toast.error(
                "Year is required"
            );

            return false;
        }

        if (
            !editId &&
            !formData.image
        ) {

            toast.error(
                "Image is required"
            );

            return false;
        }

        return true;
    };

    // ====================================
    // SUBMIT
    // ====================================
    const handleSubmit = async () => {

        try {

            if (
                !validateForm()
            ) {
                return;
            }

            const payload =
                new FormData();

            payload.append(
                "title",
                formData.title
            );

            payload.append(
                "short_description",
                formData.short_description
            );

            payload.append(
                "sport_type",
                formData.sport_type
            );

            payload.append(
                "redirect_url",
                formData.redirect_url
            );

            payload.append(
                "year",
                formData.year
            );

            payload.append(
                "is_live",
                formData.is_live
            );

            payload.append(
                "live_start_time",
                formData.live_start_time
            );

            payload.append(
                "button_text",
                formData.button_text
            );

            payload.append(
                "button_url",
                formData.button_url
            );

            payload.append(
                "status",
                formData.status
            );

            if (
                formData.image instanceof File
            ) {

                payload.append(
                    "image",
                    formData.image
                );
            }

            if (editId) {

                await apiClient.put(
                    `/api/admin/home-slider/${editId}`,
                    payload,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                toast.success(
                    "Slider updated successfully"
                );

            } else {

                await apiClient.post(
                    "/api/admin/home-slider",
                    payload,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                toast.success(
                    "Slider created successfully"
                );
            }

            getSliders();

            setOpen(false);

            setEditId(null);

            setFormData(
                initialForm
            );

        } catch (error) {

            console.log(error);

            toast.error(
                error?.response?.data
                    ?.message ||
                "Something went wrong"
            );
        }
    };

    // ====================================
    // EDIT
    // ====================================
    const handleEdit = (
        item
    ) => {

        setFormData(item);

        setEditId(item.id);

        setOpen(true);
    };

    // ====================================
    // DELETE
    // ====================================
    const handleDeleteClick = (
        id
    ) => {

        setDeleteId(id);

        setDeleteDialog(true);
    };

    const confirmDelete = async () => {

        try {

            await apiClient.delete(
                `/api/admin/home-slider/${deleteId}`
            );

            toast.success(
                "Slider deleted successfully"
            );

            getSliders();

            setDeleteDialog(false);

            setDeleteId(null);

        } catch (error) {

            console.log(error);

            toast.error(
                "Delete failed"
            );
        }
    };

    // ====================================
    // FILTER
    // ====================================
    const filteredSliders =
        sliders.filter(
            (item) => {

                const searchText =
                    search
                        ?.toLowerCase()
                        ?.trim();

                return (
                    item?.title
                        ?.toLowerCase()
                        ?.includes(
                            searchText
                        ) ||
                    item?.sport_type
                        ?.toLowerCase()
                        ?.includes(
                            searchText
                        )
                );
            }
        );

    return (
        <Box p={3}>

            <Card>

                <CardContent>

                    {/* HEADER */}

                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        justifyContent="space-between"
                        spacing={2}
                        mb={3}
                    >

                        <Typography variant="h5">
                            Slider Management
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                        >

                            <TextField
                                size="small"
                                placeholder="Search"
                                value={search}
                                onChange={(e) =>
                                    setSearch(
                                        e.target.value
                                    )
                                }
                            />

                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => {

                                    setOpen(true);

                                    setEditId(null);

                                    setFormData(
                                        initialForm
                                    );
                                }}
                            >
                                Add Slider
                            </Button>

                        </Stack>

                    </Stack>

                    {/* TABLE */}

                    <TableContainer
                        component={Paper}
                    >

                        <Table>

                            <TableHead>

                                <TableRow>

                                    <TableCell>
                                        Image
                                    </TableCell>

                                    <TableCell>
                                        Title
                                    </TableCell>

                                    <TableCell>
                                        Sport
                                    </TableCell>

                                    <TableCell>
                                        Live
                                    </TableCell>

                                    <TableCell>
                                        Status
                                    </TableCell>

                                    <TableCell>
                                        Action
                                    </TableCell>

                                </TableRow>

                            </TableHead>

                            <TableBody>

                                {loading ? (

                                    <TableRow>

                                        <TableCell
                                            colSpan={6}
                                        >

                                            <Box
                                                py={5}
                                                display="flex"
                                                justifyContent="center"
                                            >
                                                <CircularProgress />
                                            </Box>

                                        </TableCell>

                                    </TableRow>

                                ) : filteredSliders?.length > 0 ? (

                                    filteredSliders.map(
                                        (
                                            item
                                        ) => (

                                            <TableRow
                                                key={
                                                    item.id
                                                }
                                            >

                                                <TableCell>

                                                    <img
                                                        src={
                                                            imageBaseurl + item.image
                                                        }
                                                        alt=""
                                                        width={
                                                            100
                                                        }
                                                        height={
                                                            60
                                                        }
                                                        style={{
                                                            borderRadius: 10,
                                                            objectFit:
                                                                "cover",
                                                        }}
                                                    />

                                                </TableCell>

                                                <TableCell>

                                                    <Typography
                                                        fontWeight={
                                                            700
                                                        }
                                                    >
                                                        {
                                                            item.title
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                    >
                                                        {
                                                            item.year
                                                        }
                                                    </Typography>

                                                </TableCell>

                                                <TableCell>

                                                    <Chip
                                                        label={
                                                            item.sport_type
                                                        }
                                                    />

                                                </TableCell>

                                                <TableCell>

                                                    <Chip
                                                        color={
                                                            item.is_live
                                                                ? "success"
                                                                : "default"
                                                        }
                                                        label={
                                                            item.is_live
                                                                ? "LIVE"
                                                                : "OFFLINE"
                                                        }
                                                    />

                                                </TableCell>

                                                <TableCell>

                                                    <Chip
                                                        color={
                                                            item.status
                                                                ? "success"
                                                                : "error"
                                                        }
                                                        label={
                                                            item.status
                                                                ? "Active"
                                                                : "Inactive"
                                                        }
                                                    />

                                                </TableCell>

                                                <TableCell>

                                                    <Stack
                                                        direction="row"
                                                    >

                                                        <IconButton
                                                            color="primary"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <Edit />
                                                        </IconButton>

                                                        <IconButton
                                                            color="error"
                                                            onClick={() =>
                                                                handleDeleteClick(
                                                                    item.id
                                                                )
                                                            }
                                                        >
                                                            <Delete />
                                                        </IconButton>

                                                    </Stack>

                                                </TableCell>

                                            </TableRow>
                                        )
                                    )

                                ) : (

                                    <TableRow>

                                        <TableCell
                                            colSpan={6}
                                        >

                                            <Box
                                                py={5}
                                                textAlign="center"
                                            >

                                                <Typography>
                                                    No Sliders Found
                                                </Typography>

                                            </Box>

                                        </TableCell>

                                    </TableRow>

                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>

                </CardContent>

            </Card>

            {/* ADD / EDIT DIALOG */}

            <Dialog
                open={open}
                onClose={() =>
                    setOpen(false)
                }
                maxWidth="md"
                fullWidth
            >

                <DialogTitle>

                    {editId
                        ? "Edit Slider"
                        : "Add Slider"}

                </DialogTitle>

                <DialogContent>

                    <Grid
                        container
                        spacing={2}
                        mt={1}
                    >

                        <Grid
                            item
                            size={{ xs: 12, md: 6 }}

                        >

                            <TextField
                                fullWidth
                                label="Title"
                                name="title"
                                value={
                                    formData.title
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        <Grid
                            item
                            size={{ xs: 12, md: 6 }}
                        >

                            <TextField
                                fullWidth
                                label="Year"
                                name="year"
                                value={
                                    formData.year
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        <Grid
                            item
                            size={{ xs: 12, }}
                        >

                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                name="short_description"
                                value={
                                    formData.short_description
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        <Grid
                            item
                            size={{ xs: 12, md: 6 }}
                        >

                            <FormControl
                                fullWidth
                            >

                                <InputLabel>
                                    Sport Type
                                </InputLabel>

                                <Select
                                    name="sport_type"
                                    value={
                                        formData.sport_type
                                    }
                                    label="Sport Type"
                                    onChange={
                                        handleChange
                                    }
                                >

                                    <MenuItem value="football">
                                        Football
                                    </MenuItem>

                                    <MenuItem value="cricket">
                                        Cricket
                                    </MenuItem>

                                    <MenuItem value="wwe">
                                        WWE
                                    </MenuItem>

                                    <MenuItem value="ufc">
                                        UFC
                                    </MenuItem>

                                </Select>

                            </FormControl>

                        </Grid>

                        {/* <Grid
                            item
                            size={{ xs: 12, md: 6 }}
                        >

                            <TextField
                                fullWidth
                                label="Redirect URL"
                                name="redirect_url"
                                value={
                                    formData.redirect_url
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid> */}

                        <Grid
                            item
                            size={{ xs: 12, md: 6 }}
                        >

                            <TextField
                                fullWidth
                                type="datetime-local"
                                label="Live Start Time"
                                name="live_start_time"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={
                                    formData.live_start_time
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        <Grid
                            item
                            size={{ xs: 12, md: 6 }}
                        >

                            <TextField
                                fullWidth
                                label="Button Text"
                                name="button_text"
                                value={
                                    formData.button_text
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        <Grid
                            item
                            size={{ xs: 12, }}
                        >

                            <TextField
                                fullWidth
                                label="Button URL"
                                name="button_url"
                                value={
                                    formData.button_url
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </Grid>

                        {/* IMAGE */}

                        <Grid
                            item
                            size={{ xs: 12, }}
                        >

                            <Typography mb={1}>
                                Slider Image
                            </Typography>

                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                            >

                                Upload Image

                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={
                                        handleImageUpload
                                    }
                                />

                            </Button>

                            {formData.image && (

                                <Box mt={2}>

                                    <img
                                        src={
                                            formData.image instanceof File
                                                ? URL.createObjectURL(
                                                    formData.image
                                                )
                                                : imageBaseurl + formData.image
                                        }
                                        alt=""
                                        style={{
                                            width: "100%",
                                            maxHeight: 250,
                                            borderRadius: 10,
                                            objectFit:
                                                "cover",
                                        }}
                                    />

                                </Box>

                            )}

                        </Grid>

                        {/* SWITCHES */}

                        <Grid
                            item
                            size={{ xs: 12, }}
                        >

                            <Stack
                                direction="row"
                                spacing={4}
                            >

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                >

                                    <Typography>
                                        Live
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.is_live
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setFormData(
                                                (
                                                    prev
                                                ) => ({
                                                    ...prev,
                                                    is_live:
                                                        e.target.checked,
                                                })
                                            )
                                        }
                                    />

                                </Stack>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                >

                                    <Typography>
                                        Status
                                    </Typography>

                                    <Switch
                                        checked={
                                            formData.status
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setFormData(
                                                (
                                                    prev
                                                ) => ({
                                                    ...prev,
                                                    status:
                                                        e.target.checked,
                                                })
                                            )
                                        }
                                    />

                                </Stack>

                            </Stack>

                        </Grid>

                    </Grid>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setOpen(false)
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={
                            handleSubmit
                        }
                    >

                        {editId
                            ? "Update"
                            : "Create"}

                    </Button>

                </DialogActions>

            </Dialog>

            {/* DELETE DIALOG */}

            <Dialog
                open={deleteDialog}
                onClose={() =>
                    setDeleteDialog(
                        false
                    )
                }
            >

                <DialogTitle>
                    Delete Slider
                </DialogTitle>

                <DialogContent>

                    <Typography>
                        Are you sure want to
                        delete this slider?
                    </Typography>

                </DialogContent>

                <DialogActions>

                    <Button
                        onClick={() =>
                            setDeleteDialog(
                                false
                            )
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={
                            confirmDelete
                        }
                    >
                        Delete
                    </Button>

                </DialogActions>

            </Dialog>

        </Box>
    );
}